
import AppsIcon from "@mui/icons-material/Apps";
import BusinessIcon from "@mui/icons-material/Business";
import CampaignIcon from "@mui/icons-material/Campaign";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BuildIcon from "@mui/icons-material/Build";
import FeedbackIcon from "@mui/icons-material/Feedback";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LanguageIcon from "@mui/icons-material/Language";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import RouteIcon from "@mui/icons-material/Route";
import ScienceIcon from "@mui/icons-material/Science";
import SettingsIcon from "@mui/icons-material/Settings";
import TableChartIcon from "@mui/icons-material/TableChart";
import WebIcon from "@mui/icons-material/Web";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useMemo, useState } from "react";

import { apiClient } from "./api";
import { signOut } from "./auth";
import { GlobalFilters } from "./components/global-filters";
import { config, environmentLabels, Environment } from "./config";
import {
  DataStoreProvider,
  FiltersProvider,
  useDataStore,
  useFilters,
} from "./context";
import { BulkImportPage } from "./pages/app/bulk-import";
import { AiEvalsPage } from "./pages/app/ai-evals";
import { AiFeedbackPage } from "./pages/app/ai-feedback";
import { MaintenancePage } from "./pages/app/maintenance";
import { CompaniesPage } from "./pages/app/companies";
import { CopyProjectPage } from "./pages/app/copy-project";
import { DemoDeployPage } from "./pages/app/demo-deploy";
import { DashboardSection } from "./types/analytics";

// ============================================================================
// TOP-LEVEL VIEW TYPE
// ============================================================================

type TopLevelView = "landing-page" | "app";

// Page Components
import {
  AcquisitionPage,
  JourneysPage,
  OverviewPage,
  PagesPage,
  SessionsPage,
} from "./pages";

// ============================================================================
// NAVIGATION CONFIG
// ============================================================================

interface NavItem {
  id: DashboardSection;
  label: string;
  icon: React.ReactElement;
  description: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: "overview",
    label: "Overview",
    icon: <DashboardIcon />,
    description: "Site health KPIs and trends",
  },
  {
    id: "sessions",
    label: "Sessions",
    icon: <TableChartIcon />,
    description: "Raw session data table",
  },
  {
    id: "pages",
    label: "Pages",
    icon: <WebIcon />,
    description: "Page effectiveness and engagement",
  },
  {
    id: "journeys",
    label: "Journeys",
    icon: <RouteIcon />,
    description: "User paths and navigation",
  },
  {
    id: "acquisition",
    label: "Acquisition",
    icon: <CampaignIcon />,
    description: "Traffic sources and campaigns",
  },
];

// App section navigation items
type AppSection =
  | "companies"
  | "bulk-import"
  | "copy-project"
  | "demo-deploy"
  | "ai-feedback"
  | "ai-evals"
  | "maintenance";

interface AppNavItem {
  id: AppSection;
  label: string;
  icon: React.ReactElement;
  description: string;
}

const APP_NAV_ITEMS: AppNavItem[] = [
  {
    id: "companies",
    label: "Companies",
    icon: <BusinessIcon />,
    description: "Manage companies and users",
  },
  {
    id: "bulk-import",
    label: "Bulk Import",
    icon: <GroupAddIcon />,
    description: "Create multiple companies and users from spreadsheet data",
  },
  {
    id: "copy-project",
    label: "Copy Project",
    icon: <ContentCopyIcon />,
    description: "Copy projects between environments/organizations",
  },
  {
    id: "demo-deploy",
    label: "Demo Deploy",
    icon: <RocketLaunchIcon />,
    description: "Deploy demo projects and portfolios from templates",
  },
  {
    id: "ai-feedback",
    label: "AI Feedback",
    icon: <FeedbackIcon />,
    description: "Review prompt improvement suggestions and chat findings",
  },
  {
    id: "ai-evals",
    label: "AI Evals",
    icon: <ScienceIcon />,
    description: "Agent eval runs against staging, compared with a baseline",
  },
  {
    id: "maintenance",
    label: "Maintenance",
    icon: <BuildIcon />,
    description: "Lock the environment behind a maintenance page",
  },
];

// ============================================================================
// DASHBOARD CONTENT
// ============================================================================

interface DashboardContentProps {
  onLogout: () => void;
  topLevelView: TopLevelView;
  setTopLevelView: (view: TopLevelView) => void;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  onLogout,
  topLevelView,
  setTopLevelView,
}) => {
  const { activeSection, setSection } = useFilters();
  const { environment, setEnvironment, refetch } = useDataStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // App section state
  const [appSection, setAppSection] = useState<AppSection>("companies");
  const appNavItems = useMemo(
    () =>
      APP_NAV_ITEMS.filter((item) => {
        if (item.id === "ai-feedback") return config.supportsAiFeedback(environment);
        if (item.id === "maintenance") return config.supportsMaintenance(environment);
        if (item.id === "ai-evals") return config.supportsEvals(environment);
        return true;
      }),
    [environment],
  );

  useEffect(() => {
    if (appSection === "ai-feedback" && !config.supportsAiFeedback(environment)) {
      setAppSection("companies");
    }
    if (appSection === "ai-evals" && !config.supportsEvals(environment)) {
      setAppSection("companies");
    }
    if (appSection === "maintenance" && !config.supportsMaintenance(environment)) {
      setAppSection("companies");
    }
  }, [appSection, environment]);

  // Aggregation state
  const [aggregating, setAggregating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleEnvironmentChange = (
    _event: React.MouseEvent<HTMLElement>,
    newEnvironment: Environment | null,
  ) => {
    if (newEnvironment) {
      setEnvironment(newEnvironment);
    }
  };

  const handleLogout = async () => {
    await signOut();
    onLogout();
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: DashboardSection,
  ) => {
    setSection(newValue);
  };

  const handleAggregate = async () => {
    setAggregating(true);
    try {
      const result = await apiClient.triggerAggregation();
      setSnackbarMessage(
        `Aggregation complete: ${result.sessionsProcessed} sessions processed`,
      );
      setSnackbarOpen(true);
      // Refresh data after aggregation
      await refetch();
    } catch (error) {
      setSnackbarMessage(
        `Aggregation failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
      setSnackbarOpen(true);
    } finally {
      setAggregating(false);
    }
  };

  // Render the active section
  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return <OverviewPage />;
      case "sessions":
        return <SessionsPage />;
      case "pages":
        return <PagesPage />;
      case "journeys":
        return <JourneysPage />;
      case "acquisition":
        return <AcquisitionPage />;
      default:
        return <OverviewPage />;
    }
  };

  // Render the active app section
  const renderAppSection = () => {
    switch (appSection) {
      case "companies":
        return <CompaniesPage environment={environment} />;
      case "bulk-import":
        return <BulkImportPage environment={environment} />;
      case "copy-project":
        return <CopyProjectPage />;
      case "demo-deploy":
        return <DemoDeployPage environment={environment} />;
      case "ai-feedback":
        return config.supportsAiFeedback(environment) ? (
          <AiFeedbackPage environment={environment} />
        ) : (
          <CompaniesPage environment={environment} />
        );
      case "ai-evals":
        return config.supportsEvals(environment) ? (
          <AiEvalsPage />
        ) : (
          <CompaniesPage environment={environment} />
        );
      case "maintenance":
        return config.supportsMaintenance(environment) ? (
          <MaintenancePage environment={environment} />
        ) : (
          <CompaniesPage environment={environment} />
        );
      default:
        return <CompaniesPage environment={environment} />;
    }
  };

  const handleAppSectionChange = (
    _event: React.SyntheticEvent,
    newValue: AppSection,
  ) => {
    setAppSection(newValue);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* App Bar */}
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 0, mr: 2 }}>
            Admin
          </Typography>

          {/* Top-Level View Toggle */}
          <ToggleButtonGroup
            value={topLevelView}
            exclusive
            onChange={(_e, v) => v && setTopLevelView(v)}
            size="small"
            sx={{
              mr: 2,
              "& .MuiToggleButton-root": {
                color: "rgba(255,255,255,0.7)",
                borderColor: "rgba(255,255,255,0.3)",
                px: 1.5,
                "&.Mui-selected": {
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.15)",
                },
              },
            }}
          >
            <ToggleButton value="app">
              <AppsIcon sx={{ mr: 0.5, fontSize: 18 }} />
              App
            </ToggleButton>
            <ToggleButton value="landing-page">
              <LanguageIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Landing Page
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Environment Toggle */}
          <ToggleButtonGroup
            value={environment}
            exclusive
            onChange={handleEnvironmentChange}
            size="small"
            sx={{
              mr: 2,
              "& .MuiToggleButton-root": {
                color: "rgba(255,255,255,0.7)",
                borderColor: "rgba(255,255,255,0.3)",
                "&.Mui-selected": {
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.15)",
                },
              },
            }}
          >
            {config.environments.map((environment) => (
              <ToggleButton key={environment} value={environment}>
                {environmentLabels[environment].short}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {!isMobile && topLevelView === "landing-page" && (
            <Tabs
              value={activeSection}
              onChange={handleTabChange}
              textColor="inherit"
              indicatorColor="secondary"
              sx={{
                flexGrow: 1,
                "& .MuiTab-root": {
                  minWidth: "auto",
                  px: 2,
                  opacity: 0.85,
                  "&.Mui-selected": {
                    opacity: 1,
                  },
                },
              }}
            >
              {NAV_ITEMS.map((item) => (
                <Tab
                  key={item.id}
                  value={item.id}
                  label={item.label}
                  icon={item.icon}
                  iconPosition="start"
                  sx={{ textTransform: "none" }}
                />
              ))}
            </Tabs>
          )}

          {!isMobile && topLevelView === "app" && (
            <Tabs
              value={appSection}
              onChange={handleAppSectionChange}
              textColor="inherit"
              indicatorColor="secondary"
              sx={{
                flexGrow: 1,
                "& .MuiTab-root": {
                  minWidth: "auto",
                  px: 2,
                  opacity: 0.85,
                  "&.Mui-selected": {
                    opacity: 1,
                  },
                },
              }}
            >
              {appNavItems.map((item) => (
                <Tab
                  key={item.id}
                  value={item.id}
                  label={item.label}
                  icon={item.icon}
                  iconPosition="start"
                  sx={{ textTransform: "none" }}
                />
              ))}
            </Tabs>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Aggregate Button (Landing Page only) */}
          {topLevelView === "landing-page" && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleAggregate}
              disabled={aggregating}
              startIcon={
                aggregating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              sx={{
                mr: 2,
                borderColor: "rgba(255,255,255,0.5)",
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              {aggregating ? "Aggregating..." : "Aggregate"}
            </Button>
          )}

          {/* Settings (future) */}
          <Tooltip title="Settings">
            <IconButton color="inherit" sx={{ opacity: 0.7 }}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {/* Logout */}
          <Tooltip title="Sign out">
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation - Landing Page */}
      {isMobile && topLevelView === "landing-page" && (
        <Tabs
          value={activeSection}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {NAV_ITEMS.map((item) => (
            <Tab
              key={item.id}
              value={item.id}
              label={item.label}
              icon={item.icon}
              iconPosition="start"
              sx={{ textTransform: "none", minWidth: "auto" }}
            />
          ))}
        </Tabs>
      )}

      {/* Mobile Navigation - App */}
      {isMobile && topLevelView === "app" && (
        <Tabs
          value={appSection}
          onChange={handleAppSectionChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {appNavItems.map((item) => (
            <Tab
              key={item.id}
              value={item.id}
              label={item.label}
              icon={item.icon}
              iconPosition="start"
              sx={{ textTransform: "none", minWidth: "auto" }}
            />
          ))}
        </Tabs>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          {topLevelView === "landing-page" ? (
            <>
              {/* Global Filters (Landing Page only) */}
              <GlobalFilters />
              {/* Active Section Content */}
              {renderSection()}
            </>
          ) : (
            /* App Content */
            renderAppSection()
          )}
        </Container>
      </Box>

      {/* Aggregation Status Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

interface DashboardProps {
  onLogout: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [topLevelView, setTopLevelView] = useState<TopLevelView>("app");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FiltersProvider>
        <DataStoreProvider>
          <DashboardContent
            onLogout={onLogout}
            topLevelView={topLevelView}
            setTopLevelView={setTopLevelView}
          />
        </DataStoreProvider>
      </FiltersProvider>
    </LocalizationProvider>
  );
};
